import { getDataReaderClient, getModelDescriptionsClient } from "@multiweb/clients/util";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { ISignatureTemplate } from "../../Global/Base/Crypto/ISignatureInfo";
import { EncAttrValue } from "../../Global/Base/DataTypes/EncAttrValue";
import { OWTime } from "../../Global/Base/DataTypes/OWTime";
import { InvalidOperationException } from "../../Global/Base/Exceptions";
import { DateUtils } from "../../Global/Base/Utils/DateUtils";
import { AttrValueReader } from "../../OW.Remoting/Remoting/AttrValueReader";
import { Enum } from "../../OW.Remoting/Remoting/DataTypes/Enums";
import { OWDocumentDescription } from "../../OW.Remoting/Remoting/DataTypes/OWDocumentDescription";
import { NullOWObject, OWObject } from "../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../OW.Remoting/Remoting/RequestContext";

const logger = LazyLoadingSectionLogger.create(LoggingSection.encryption);

/** Vytváří serializovanou reprezentaci objektu pro podpis */
export class ObjectSignatureSerializer {
    /**
      * Vytváří reprezentaci dané instance aplikačního záznamu určenou pro podepsání
      * @param object lidský název záznamu zobrazený uživateli
      * @param template Šablona, na základě níž má být vytvořena nová reprezentace
      */
    public static async mapSignDataToTemplateJSON(object: OWObject, template: ISignatureTemplate, requestContext?: RequestContext): Promise<object> {
        const f_loadAttrs = async (className: string, attrs: string[]): Promise<{ [attrName: string]: IPublicAttributeInfo }> => {
            const attrInfo = await getModelDescriptionsClient().getAttributesPublicInfo(className, attrs, requestContext?.cancellationToken);
            const attrInfoData: { [attrName: string]: IPublicAttributeInfo } = {};
            for (const aInfo of attrInfo) {
                attrInfoData[aInfo.attributeName] = aInfo;
            }

            return attrInfoData;
        };

        const refObj: { [objectName: string]: { [attrName: string]: any } } = {};

        let objCnt = 2;
        refObj[object.objectName + "#1"] = await this._loadAttrDataToMap(template.attributes, object, await f_loadAttrs(template.className, template.attributes), requestContext);

        if (template.associatedCollections && template.associatedCollections.length > 0) {
            for (const item of template.associatedCollections) {
                let params: any = undefined;
                const condParams = item.conditionParams ? item.conditionParams.split(";") : undefined;
                if (condParams && condParams.length > 0) {
                    try {
                        params = await object.getParameters(condParams);
                    } catch (err) {
                        logger.error(err);
                        throw new InvalidOperationException("Unable load parameters");
                    }
                }

                const res = await getDataReaderClient().getDataRowsWithParams(item.className, ["ID"], item.condition, { params: params, startIndex: 0, orderBy: ["ID"] });

                const attrList = await f_loadAttrs(item.className, item.attributes);
                const dataOfObjectsFetching: Promise<object>[] = [];

                for (const row of res)
                    dataOfObjectsFetching.push(OWManager.getObject(AttrValueReader.read(row.ID), item.className)
                        .then((obj) => this._loadAttrDataToMap(item.attributes, obj, attrList, requestContext)));

                await Promise.all(dataOfObjectsFetching);

                for (const dataFetch of dataOfObjectsFetching)
                    refObj[item.className + "#" + objCnt++] = await dataFetch;
            }
        }

        return refObj;
    }

    /**
     * Namapuje atributy do struktury pro podpis objektu
     * @param attrs Atributy k namapování
     * @param obj Objekt k získání hodnoty z atributu
     * @param attrInfo Informace k atributu
     * @param requestContext Viz {@link RequestContext}
     */
    private static async _loadAttrDataToMap(attrs: string[], obj: OWObject, attrInfo: { [attrName: string]: IPublicAttributeInfo }, requestContext?: RequestContext): Promise<{ [p: string]: string | number | boolean | null }> {
        const objData: { [attrName: string]: string | number | boolean | null } = {};

        for (const attr of attrs) {
            objData[attr] = await this._getAttrJsonValue(OWManager.getFinalAttribute(attr), attrInfo[attr].type, requestContext, await obj.getValue(attr));
        }

        return objData;
    }

    /**
     * Provádí konverzi vstupní hodnoty na formát vhodný pro JSON
     * @param attrName Název atributu
     * @param dataType Datový typ
     * @param requestContext Viz {@link RequestContext}
     * @param value Konvertovaná hodnota
     */
    private static async _getAttrJsonValue(attrName: string, dataType: OWDataType, requestContext?: RequestContext, value: any = null): Promise<string | number> {
        if (value == null)
            return null;

        let valueEncrypted = false;
        if (value instanceof EncAttrValue) {
            if (value.isEncrypted)
                throw new Error(`Cannot get Value of Encrypted attribute '${attrName}'. Value must be decrypted.`);

            valueEncrypted = true;
            value = value.valueOf();

            if (value == null)
                return null;
        }

        if (value instanceof OWTime) {
            const date = new Date(value.getTime());
            date.setFullYear(100); // .NET má defaultně rok 100

            return DateUtils.dateToString(date, "HH:mm:ss");
        }

        if (value instanceof NullOWObject)
            return null;

        if (attrName.toUpperCase() === "ID")
            return value ? value.toString() : null;

        switch (dataType) {
            case OWDataType.boolean:
                return value;
            case OWDataType.OWObject:
                return (value as OWObject).isNew ? null : (value as OWObject).id.toString();
            case OWDataType.document: {
                const metadata = await (value as OWDocumentDescription).getMetadata(requestContext);
                return valueEncrypted ? metadata.originalHash : metadata.hash;
            }
            case OWDataType.dateTime:
                return DateUtils.dateToString(value, "yyyy-MM-dd HH:mm:ss");
            case OWDataType.date:
                return DateUtils.dateToString(value, "yyyy-MM-dd");
            case OWDataType.flags:
            case OWDataType.enum:
                return (value as Enum).getName();
            case OWDataType.localizedText:
            case OWDataType.strNum:
            case OWDataType.htmlText:
            case OWDataType.icon:
            case OWDataType.timeSpan:
            case OWDataType.binary:
                throw new InvalidOperationException(`Unsupported data type (${dataType})`); //`
            default:
                return this._fixNewLinesInJson(value.toString());
        }
    }

    /** Nahradí \r\n za \n */
    private static _fixNewLinesInJson(val: string): string {
        return val.replace(/\r+[\n]?/g, "\n");
    }
}
